import {
  resetUserUpdateStatus,
  updateUserAttributes,
  userState,
  userUpdatePassword,
} from "@/store/userState.ts";
import { Button } from "../../../../components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog.tsx";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
} from "../../../../components/ui/form.tsx";
import { Input } from "../../../../components/ui/input.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useEffect, type FC } from "react";
import { useForm, type ControllerRenderProps } from "react-hook-form";
import z, { ZodObject } from "zod";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp.tsx";
import {
  confirmNewEmail,
  confirmNewEmailState,
  resetConfirmNewEmailState,
} from "@/store/confirmNewEmailState.ts";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";

type Props = {
  type: "fullname" | "oldPassword" | "email";
  defaultValue?: string;
  open: boolean;
  onClose: () => void;
};

const generateFormSchema = (
  type: "fullname" | "email" | "oldPassword",
): ZodObject => {
  if (type === "email")
    return z.object({ email: z.email({ error: "Email invalide" }) });

  return z.object({
    fullname: z
      .string()
      .min(2, { error: "Le nom complet doit contenir au moins 2 lettres" }),
  });
};

const formNewPasswordSchema = z.object({
  oldPassword: z
    .string()
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      {
        error: "Mot de passe invalide",
      },
    ),
  newPassword: z
    .string()
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      {
        error: "Mot de passe invalide",
      },
    ),
});

const formCodeSchema = z.object({
  code: z.string().min(6),
});

const getMessageError = (
  error: Error,
  t: TFunction<"translation", undefined>,
): string => {
  if (["NetworkError"].includes(error.message.split(":")[0]))
    return t("network_error");

  if (
    error.message ===
    "CodeMismatchException: Invalid verification code provided, please try again."
  )
    return t("invalid_code");

  if (
    error.message === "NotAuthorizedException: Incorrect username or password."
  )
    return t("incorrect_password");

  if (
    error.message ===
    "LimitExceededException: Attempt limit exceeded, please try after some time."
  )
    return t("retries_limit_exceeded");

  return error.message;
};

const EditProfile: FC<Props> = ({ type, defaultValue = "", open, onClose }) => {
  const { t } = useTranslation();
  const formSchema = generateFormSchema(type);
  const { userUpdated, userUpdating, codeSent, userUpdateError, data } =
    userState();
  const { error: codeError } = confirmNewEmailState();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      [type]: defaultValue,
    },
  });

  const formCode = useForm<z.infer<typeof formCodeSchema>>({
    resolver: zodResolver(formCodeSchema),
    defaultValues: {
      code: "",
    },
  });

  const formNewPassword = useForm<z.infer<typeof formNewPasswordSchema>>({
    resolver: zodResolver(formNewPasswordSchema),
    defaultValues: {
      oldPassword: "",
      newPassword: "",
    },
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    updateUserAttributes({
      key: type === "email" ? "email" : "name",
      value: values[type] as string,
    });
  };

  const handleCodeSubmit = (values: z.infer<typeof formCodeSchema>) => {
    confirmNewEmail({ ...values, email: form.getValues("email") as string });
  };

  const handlePasswordSubmit = (
    values: z.infer<typeof formNewPasswordSchema>,
  ) => {
    userUpdatePassword(values);
  };

  useEffect(() => {
    if (userUpdated) {
      if (type === "email") {
        toast.success(t("user_updated_email"), {
          duration: 2000,
        });
      }
      if (type === "fullname") {
        toast.success(t("user_updated_fullname"), {
          duration: 2000,
        });
      }
      if (type === "oldPassword") {
        toast.success(t("user_updated_password"), {
          duration: 2000,
        });
      }
      resetUserUpdateStatus();
      resetConfirmNewEmailState();
      onClose();
    }
  }, [userUpdated]);

  useEffect(() => {
    if (codeSent && data) {
      toast.success(t("user_updated_code_sent", { email: data.email }), {
        duration: 2000,
      });
    }
  }, [codeSent, data]);

  return (
    <Dialog open={open}>
      <DialogContent showCloseButton={false} className="sm:w-[335px]">
        <DialogHeader>
          <DialogTitle>{t("edit_profil")}</DialogTitle>
          <VisuallyHidden>
            <DialogDescription>Edit the profile</DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        {type !== "oldPassword" && (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="mt-[8px]"
            >
              <FormField
                control={form.control}
                name={type}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t(type)}</FormLabel>
                    <FormControl>
                      <Input
                        {...(field as ControllerRenderProps<
                          Record<string, string>
                        >)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-between mt-[32px] gap-[32px] w-[100%]">
                <Button
                  variant="secondaryFilled"
                  style={{ width: "calc(50% - 16px)" }}
                  onClick={onClose}
                  type="button"
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="primaryFilled"
                  style={{ width: "calc(50% - 16px)" }}
                  disabled={!form.formState.isValid || userUpdating}
                >
                  {t("validate")}
                </Button>
              </div>
            </form>
          </Form>
        )}

        {type === "oldPassword" && (
          <Form {...formNewPassword}>
            <form onSubmit={formNewPassword.handleSubmit(handlePasswordSubmit)}>
              <FormField
                control={formNewPassword.control}
                name="oldPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mot de passe actuel</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={formNewPassword.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nouveau mot de passe</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-between mt-[32px] gap-[32px] w-[100%]">
                <Button
                  variant="secondaryFilled"
                  style={{ width: "calc(50% - 16px)" }}
                  onClick={onClose}
                  type="button"
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="primaryFilled"
                  style={{ width: "calc(50% - 16px)" }}
                  disabled={!formNewPassword.formState.isValid || userUpdating}
                >
                  {t("validate")}
                </Button>
              </div>
            </form>
          </Form>
        )}
        {codeSent && (
          <Form {...formCode}>
            <form onSubmit={formCode.handleSubmit(handleCodeSubmit)}>
              <FormField
                control={formCode.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <InputOTP maxLength={6} {...field}>
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                disabled={
                  !formCode.formState.isValid || !form.formState.isValid
                }
                className="mt-[8px]"
              >
                {t("validate_code")}
              </Button>
            </form>
          </Form>
        )}
        {userUpdateError && (
          <Alert variant="destructive">
            <AlertTitle>{t("error_title_update_profile")}</AlertTitle>
            <AlertDescription>
              {getMessageError(userUpdateError, t)}
            </AlertDescription>
          </Alert>
        )}
        {codeError && (
          <Alert variant="destructive">
            <AlertTitle>{t("error_title_update_profile")}</AlertTitle>
            <AlertDescription>{getMessageError(codeError, t)}</AlertDescription>
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditProfile;
